import {
  ComponentRef,
  createComponent,
  Directive,
  EnvironmentInjector,
  inject,
  Injector,
  Input,
  SimpleChanges,
} from '@angular/core';

import { BehaviorSubject, Subject, switchMap } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';

import { ListItemResolverDirective } from '../../default-templates/list-item/list-item-resolver.directive';
import { Select2ListItemDirective } from '../../default-templates/list-item/list-item.directive';
import {
  SELECT_FILTERED_ITEMS,
  SELECT_ITEMS,
  SelectController,
} from '../../select-controller.service';
import { Select2Item } from '../../types';
import { GroupsListItemWrapperComponent } from './list-item-wrapper/groups-list-item-wrapper.component';
import { GROUPS_FEATURE, GroupsFeatureDir } from './types';

export interface Select2GroupWithItems<T extends Select2Item = Select2Item> {
  name: string;
  items: T[];
}

@Directive({
  standalone: true,
  selector: 'rt-select2[withGroups]',
  providers: [
    { provide: SELECT_FILTERED_ITEMS, useFactory: () => new BehaviorSubject<Select2Item[]>([]) },
    { provide: SELECT_ITEMS, useFactory: () => new BehaviorSubject<Select2Item[]>([]) },
    { provide: GROUPS_FEATURE, useExisting: GroupsFeatureDirective },
  ],
})
export class GroupsFeatureDirective implements GroupsFeatureDir {
  @Input() public groupedItems: Select2GroupWithItems[];
  @Input() public items: Select2Item[];

  public groupedItems$ = new BehaviorSubject<Select2GroupWithItems[]>([]);
  public headers: Record<number, string> = {};
  public listItem: Select2ListItemDirective<any>;

  private wrapperRef?: ComponentRef<GroupsListItemWrapperComponent>;
  private ngOnDestroy$ = new Subject<void>();
  private filteredItems$ = inject(SELECT_FILTERED_ITEMS);
  private items$ = inject(SELECT_ITEMS);

  constructor(
    public selectController: SelectController,
    private listItemResolver: ListItemResolverDirective,
    private injector: Injector,
    private envInjector: EnvironmentInjector,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.groupedItems) {
      this.items$.next(this.groupedItems.flatMap(group => group.items));
      this.groupedItems$.next(this.groupedItems);
    }

    if (changes.items) {
      throw new Error('Use groupedItems input');
    }
  }

  public ngOnInit() {
    this.selectController.itemsRefresh$
      .pipe(
        startWith(null),
        switchMap(() => this.groupedItems$),
        debounceTime(0),
        map(groupedItems => {
          this.headers = {};
          let nextGroupIndex = 0;
          return groupedItems.flatMap(group => {
            const filteredGroup = this.selectController.filterItems(group.items);
            const groupSize = filteredGroup.length;
            if (groupSize) {
              this.headers = {
                ...this.headers,
                [nextGroupIndex]: group.name,
              };
              nextGroupIndex += groupSize;
            }
            return filteredGroup;
          });
        }),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(items => this.filteredItems$.next(items));
  }

  public ngAfterContentInit() {
    this.wrapperRef = createComponent(GroupsListItemWrapperComponent, {
      environmentInjector: this.envInjector,
      elementInjector: this.injector,
    });
    this.wrapperRef.changeDetectorRef.detectChanges();
    this.selectController.listItemDirective = this.wrapperRef.instance.directive;
    this.listItem = this.listItemResolver.resolve();
    this.selectController.cdRef.detectChanges();
  }

  public ngOnDestroy() {
    this.wrapperRef?.destroy();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
